// pages/ProactiveMessages/ProactiveMessagesSettings.js
import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axiosInstance from '../../../utils/axiosInstance';
import { 
    Divider,
    Box, 
    Typography, 
    Alert, 
    Paper, 
    TextField, 
    Button, 
    List, 
    ListItem, 
    CircularProgress,
    Tooltip, 
    Switch, 
    FormControl, 
    Select, 
    MenuItem,
    IconButton,
    Container,
    useTheme
} from '@mui/material';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { hasPermission } from '../../../utils/permissionUtils';

const MAX_MESSAGES = 5;
const DELAY_OPTIONS = [30, 60, 90, 120];

export default function ProactiveMessagesSettings() {

    const theme = useTheme();
    const { chatbotId } = useParams();
    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');

    const [canView, setCanView] = useState(false);
    const [canModify, setCanModify] = useState(false);
    const [canDelete, setCanDelete] = useState(false);
    const [checkingPermissions, setCheckingPermissions] = useState(true);

    useEffect(() => {
        const checkPermissions = async () => {
            setCheckingPermissions(true);
            try {
                const viewPerm = await hasPermission('proactive-messages', 'view');
                const modifyPerm = await hasPermission('proactive-messages', 'add|edit');
                const deletePerm = await hasPermission('proactive-messages', 'delete');

                setCanView(viewPerm);
                setCanModify(modifyPerm);
                setCanDelete(deletePerm);
            } catch (error) {
                console.error('Error checking permissions:', error);
                setCanView(false);
                setCanModify(false);
                setCanDelete(false);
            } finally {
                setCheckingPermissions(false);
            }
        };

        checkPermissions();
    }, []);

    useEffect(() => {
        document.title = 'Proactive Behavior | KlonIT';
        fetchMessages();
    }, [chatbotId]);

    const fetchMessages = useCallback(async () => {
        setIsLoading(true);
        try {
            const response = await axiosInstance.get(`/api/chatbots/${chatbotId}/proactive-messages`);
            setMessages(response.data.data || []);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to fetch messages.');
        } finally {
            setIsLoading(false);
        }
    }, [chatbotId]);

    const handleAddMessage = async (e) => {
        e.preventDefault();
        if (!newMessage.trim() || messages.length >= MAX_MESSAGES) return;

        setIsSubmitting(true);
        setError('');
        try {
            await axiosInstance.post(`/api/chatbots/${chatbotId}/proactive-messages`, {
                message_text: newMessage,
            });
            setNewMessage('');
            fetchMessages();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to add message.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDeleteMessage = async (messageId) => {
        setError('');
        try {
            await axiosInstance.delete(`/api/proactive-messages/${messageId}`);
            fetchMessages();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to delete message.');
        }
    };

    const handleDragEnd = async (result) => {
        if (!result.destination) return;

        const items = Array.from(messages);
        const [reorderedItem] = items.splice(result.source.index, 1);
        items.splice(result.destination.index, 0, reorderedItem);

        setMessages(items);

        try {
            const ordered_ids = items.map(m => m.id);
            await axiosInstance.put(`/api/chatbots/${chatbotId}/proactive-messages/reorder`, {
                ordered_ids
            });
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to update message order.');
            fetchMessages();
        }
    };

    const handleToggleActive = async (messageId, currentState) => {
        try {
            await axiosInstance.put(`/api/proactive-messages/${messageId}/settings`, {
                is_active: !currentState
            });
            fetchMessages();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to update message status.');
        }
    };

    const handleDelayChange = async (messageId, newDelay) => {
        try {
            await axiosInstance.put(`/api/proactive-messages/${messageId}/settings`, {
                delay_seconds: parseInt(newDelay)
            });
            fetchMessages();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to update delay setting.');
        }
    };

    if (checkingPermissions) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", p: 5 }}>
                <Typography>Checking permissions...</Typography>
            </Box>
        );
    }

    if (!canView) {
        return (
            <Container>
                <Alert severity="error" sx={{ mt: 6 }}>
                    <strong>Access Denied</strong>
                    <br />
                    You don't have permission to view proactive message settings. Please contact your administrator.
                </Alert>
            </Container>
        );
    }

    return (
        <Paper
            sx={{
            p: { xs: 1.5, sm: 3 },
            mt: { xs: 2, sm: 10 },
            mx: "auto",
            maxWidth: "1200px",
            bgcolor: "background.paper",
            boxShadow: theme.palette.mode === "dark" ? 4 : 2,
            }}
        >
            <Box sx={{ mb: 3 }}>
                <Typography variant="h4" component="h1" fontWeight="bold" gutterBottom color="textPrimary" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
                    Proactive Behavior
                </Typography>
                <Divider sx={{ mb: 3, borderColor: "divider" }} />

                
                <Typography variant="body2" color="textPrimary">
                    Add messages to automatically re-engage users after configured inactivity periods.
                </Typography>
            </Box>

            <Paper sx={{ p: { xs: 1.5, sm: 3 }, mx: 'auto' }} elevation={1}>
                <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                    <Typography variant="h6" gutterBottom color="textPrimary">
                        Manage Proactive Messages
                    </Typography>
                    <Typography variant="body2" color="textPrimary">
                        You can add up to {MAX_MESSAGES} messages. The Chatbot will show messages in sequential order. 
                        Drag to reorder, set delay timing, and toggle to activate/deactivate.
                    </Typography>
                </Box>

                <Box sx={{ p: 3 }}>
                    {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

                    <Box component="form" onSubmit={handleAddMessage} sx={{ mb: 3 }}>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <TextField
                                fullWidth
                                size="small"
                                placeholder="Enter a new proactive message..."
                                value={newMessage}
                                onChange={(e) => setNewMessage(e.target.value)}
                                disabled={messages.length >= MAX_MESSAGES || isSubmitting}
                                inputProps={{ maxLength: 500 }}
                            />
                            {canModify && (
                                <Button
                                    type="submit"
                                    variant="contained"
                                    disabled={!newMessage.trim() || messages.length >= MAX_MESSAGES || isSubmitting}
                                    sx={{ minWidth: 80 }}
                                >
                                    {isSubmitting ? <CircularProgress size={20} /> : 'Add'}
                                </Button>
                            )}
                        </Box>
                        {messages.length >= MAX_MESSAGES && (
                            <Typography variant="caption" color="warning.main" sx={{ mt: 1, display: 'block' }}>
                                You have reached the maximum limit of {MAX_MESSAGES} messages.
                            </Typography>
                        )}
                    </Box>

                    <Typography variant="subtitle2" gutterBottom color="textPrimary">
                        Your Messages ({messages.length}/{MAX_MESSAGES})
                    </Typography>

                    {isLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <DragDropContext onDragEnd={handleDragEnd}>
                            <Droppable droppableId="messages">
                                {(provided) => (
                                    <List
                                        {...provided.droppableProps}
                                        ref={provided.innerRef}
                                        sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}
                                    >
                                        {messages.length > 0 ? (
                                            messages.map((m, index) => (
                                                <Draggable key={m.id} draggableId={String(m.id)} index={index}>
                                                    {(provided) => (
                                                        <ListItem
                                                            ref={provided.innerRef}
                                                            {...provided.draggableProps}
                                                            {...provided.dragHandleProps}
                                                            sx={{
                                                                borderBottom: index < messages.length - 1 ? 1 : 0,
                                                                borderColor: 'divider',
                                                                bgcolor: 'background.paper'
                                                            }}
                                                        >
                                                            <Box sx={{ display: 'flex', alignItems: { xs: 'flex-start', sm: 'center' }, width: '100%', gap: 1, flexDirection: { xs: 'column', sm: 'row' } }}>
                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
                                                                    <DragIndicatorIcon sx={{ color: 'textPrimary', cursor: 'grab', flexShrink: 0 }} />
                                                                    <Typography
                                                                        sx={{
                                                                            flexGrow: 1,
                                                                            color: !m.is_active ? 'text.disabled' : 'textPrimary',
                                                                            wordBreak: 'break-word',
                                                                        }}
                                                                    >
                                                                        {m.message_text}
                                                                    </Typography>
                                                                </Box>
                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                                                                    {canModify && (
                                                                        <>
                                                                            <FormControl size="small" sx={{ minWidth: 100 }}>
                                                                                <Select
                                                                                    value={m.delay_seconds}
                                                                                    onChange={(e) => handleDelayChange(m.id, e.target.value)}
                                                                                    displayEmpty
                                                                                >
                                                                                    {DELAY_OPTIONS.map(delay => (
                                                                                        <MenuItem key={delay} value={delay}>
                                                                                            {delay}s
                                                                                        </MenuItem>
                                                                                    ))}
                                                                                </Select>
                                                                            </FormControl>
                                                                            <Tooltip title={m.is_active ? "Deactivate" : "Activate"} arrow>
                                                                                <Switch
                                                                                    checked={m.is_active}
                                                                                    onChange={() => handleToggleActive(m.id, m.is_active)}
                                                                                    color="primary"
                                                                                />
                                                                            </Tooltip>
                                                                        </>
                                                                    )}
                                                                    {canDelete && (
                                                                        <Tooltip title="Delete Message" arrow>
                                                                            <IconButton
                                                                                size="small"
                                                                                color="error"
                                                                                onClick={() => handleDeleteMessage(m.id)}
                                                                            >
                                                                                <DeleteOutlineIcon />
                                                                            </IconButton>
                                                                        </Tooltip>
                                                                    )}
                                                                </Box>
                                                            </Box>
                                                        </ListItem>
                                                    )}
                                                </Draggable>
                                            ))
                                        ) : (
                                            <ListItem>
                                                <Typography color="textPrimary" sx={{ width: '100%', textAlign: 'center' }}>
                                                    No proactive messages added yet.
                                                </Typography>
                                            </ListItem>
                                        )}
                                        {provided.placeholder}
                                    </List>
                                )}
                            </Droppable>
                        </DragDropContext>
                    )}
                </Box>
            </Paper>
        </Paper>
    );
}